
import React, { useEffect, useState } from 'react';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { UseFormReturn } from "react-hook-form";
import { AgeGroupVariationFormData } from "../schemas/ageGroupVariationSchema";
import { OfferingOption } from '../../hooks/useOfferingOptions';
import { MultiColumnCheckboxes } from './MultiColumnCheckboxes';
import { RequiredFieldLabel } from '@/components/ui/required-field-label';

interface BasicInfoFieldsProps {
  form: UseFormReturn<AgeGroupVariationFormData>;
  offeringOptions: OfferingOption[];
  existingKeys?: string[];
  isEditMode?: boolean;
  /** Placeholder for key field (e.g. "g-camp-option1" for shared programs). */
  keyPlaceholder?: string;
}

export const BasicInfoFields: React.FC<BasicInfoFieldsProps> = ({
  form,
  offeringOptions,
  existingKeys = [],
  isEditMode = false,
  keyPlaceholder
}) => {
  const [hasDuplicateKey, setHasDuplicateKey] = useState(false);
  
  // Watch the key field to check for duplicates
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'key' || name === undefined) {
        const currentKey = form.getValues('key');
        
        if (currentKey && existingKeys.includes(currentKey)) {
          setHasDuplicateKey(true);
        } else {
          setHasDuplicateKey(false);
        }
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form, existingKeys]);
  
  // Format and validate the key input
  const handleKeyInput = (e: React.FormEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    
    // Remove any characters that don't match the pattern
    const formatted = input.value.replace(/[^a-zA-Z0-9_-]/g, '');
    
    // Update the input value if it changed
    if (input.value !== formatted) {
      input.value = formatted;
      
      // Trigger the onChange event for React Hook Form
      const event = new Event('input', { bubbles: true });
      input.dispatchEvent(event);
    }
    
    // Check for duplicates
    if (formatted && existingKeys.includes(formatted)) {
      setHasDuplicateKey(true);
    } else {
      setHasDuplicateKey(false);
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="displayTitle"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-left block">
                <RequiredFieldLabel label="Program name" /></FormLabel>
              <FormControl>
                <Input 
                  placeholder="Example: Morning Camp for Ages 8-12" 
                  {...field} 
                  value={field.value || ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="key"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-left block">
                <RequiredFieldLabel label="Internal ID" />
              </FormLabel>
              <FormControl>
                <Input 
                  placeholder={keyPlaceholder ?? "A short unique ID, example: Morning 8-12"} 
                  {...field}
                  disabled={isEditMode}
                  maxLength={40}
                  onInput={handleKeyInput}
                />
              </FormControl>
              {hasDuplicateKey && (
                <div className="text-red-500 text-sm mt-1">Internal ID already in use</div>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

      </div>

      <FormField
        control={form.control}
        name="offeringTypeKeys"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-left block">
              <RequiredFieldLabel label="Offering types" />
            </FormLabel>
            <FormControl>
              <MultiColumnCheckboxes
                options={offeringOptions || []}
                selectedValues={field.value || []}
                onChange={field.onChange}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
